import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Segmented, Tag } from 'antd';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getVentesManquees, getGeoZones } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker, NdoeActionButton,
  NdoeResponsiveTable, NdoeImage, NdoeStats, NdoeStatCard,
  NdoeError, NdoeLoading, NdoeArticleLink, useFicheArticle,
} from '../../components/ndoe';
import { money } from './format';
import './rapports.css';

// Barre de part du CA (réutilise le style anc-partbar).
const PartBar = ({ value }) => (
  <div className="anc-partbar" title={`${value} % du CA`}>
    <div className="anc-partbar__track">
      <i style={{ width: `${Math.max(2, Math.min(100, Number(value) || 0))}%` }} />
    </div>
    <span className="num">{value} %</span>
  </div>
);

export default function PertesEtZones() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const navigate = useNavigate();
  // Le nom et la référence d'une paire mènent à sa fiche — c'est déjà le cas
  // sur 6 rapports, ce ne l'était pas ici (signalé le 31/07/2026).
  const { openFiche, ficheElement } = useFicheArticle();

  const [vue, setVue] = useState('pertes');           // pertes | zones
  const [period, setPeriod] = useState({ period: 90 });
  // payload est ÉTIQUETÉ par le volet qui l'a produit : sans ça, basculer de
  // volet pendant une période « Perso » incomplète (aucune requête ne part)
  // affichait les données de l'AUTRE volet — jusqu'à un faux « aucune vente
  // manquée ». On ne rend que si payload.pour === vue.
  const [payload, setPayload] = useState(null);       // { pour: 'pertes'|'zones', data }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const apiParams = useMemo(() => ({
    period: period.period === 'custom' ? undefined : period.period,
    start_date: period.period === 'custom' ? period.start_date : undefined,
    end_date: period.period === 'custom' ? period.end_date : undefined,
  }), [period]);

  // Garde de séquence (même motif que l'analyse dimensions) : seule la dernière
  // requête écrit l'état — un basculement rapide de vue ne mélange rien.
  const reqSeq = useRef(0);
  const load = useCallback(async () => {
    if (period.period === 'custom' && !(period.start_date && period.end_date)) { setLoading(false); return; }
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const { data } = vue === 'zones'
        ? await getGeoZones(apiParams)
        : await getVentesManquees(apiParams);
      if (seq !== reqSeq.current) return;
      setPayload({ pour: vue, data });
      setError('');
    } catch (e) {
      if (seq !== reqSeq.current) return;
      setError(e.response?.data?.error || 'Impossible de charger le rapport.');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [vue, apiParams, period]);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  // Données rendues UNIQUEMENT si elles viennent du volet affiché.
  const donnees = payload && payload.pour === vue ? payload.data : null;
  const s = donnees?.summary || {};

  // --- Volet A : ventes manquées ---------------------------------------------
  const colsPertes = [
    { title: 'Produit', key: 'p', render: (t, r) => (
      <div className="pz-produit">
        <NdoeImage src={r.img} alt={r.nom_produit || 'Produit'} size="sm" />
        <div className="pz-produit__txt">
          {/* Le nom ET la référence mènent à la fiche : on reconnaît une paire
              tantôt à l'un, tantôt à l'autre — n'en rendre qu'un cliquable
              revient à parier sur lequel. */}
          <NdoeArticleLink onClick={() => openFiche(r.id_produit)}>{r.nom_produit}</NdoeArticleLink>
          <small>
            {r.code_produit && (
              <NdoeArticleLink onClick={() => openFiche(r.id_produit)}>{r.code_produit}</NdoeArticleLink>
            )}
            {r.code_produit && r.marque ? ' · ' : ''}{r.marque || ''}
          </small>
        </div>
      </div>
    ) },
    { title: 'Pointures en rupture', key: 'pts', render: (t, r) => (
      <div className="pz-chips">
        {(r.pointures_manquantes || []).map((pt, i) => (
          <Tag key={i} color="volcano">P{pt.taille} · {pt.jours_rupture} j</Tag>
        ))}
      </div>
    ) },
    { title: 'Paires manquées (est.)', dataIndex: 'ventes_manquees', key: 'vm', align: 'right', render: (v) => <b className="num">{v}</b> },
    { title: 'Prix moyen', dataIndex: 'prix_moyen', key: 'pm', align: 'right', render: (v) => <span className="num">{v == null ? '—' : money(v)}</span> },
    { title: 'CA manqué (est.)', dataIndex: 'ca_manque_estime', key: 'cm', align: 'right', render: (v) => <b className="num pz-perte">{v == null ? '—' : money(v)}</b> },
    { title: 'Vendu (période)', dataIndex: 'quantite_vendue', key: 'qv', align: 'right', render: (v) => <span className="num">{v}</span> },
  ];

  // --- Volet B : zones --------------------------------------------------------
  const colsZones = [
    { title: 'Commune', dataIndex: 'commune', key: 'c', render: (v) => <b>{v}</b> },
    { title: 'Part du CA', key: 'part', render: (t, r) => <PartBar value={r.part_ca} /> },
    { title: 'CA', dataIndex: 'ca', key: 'ca', align: 'right', render: (v) => <span className="num">{money(v)}</span> },
    { title: 'Acheteuses', dataIndex: 'acheteuses', key: 'a', align: 'right', render: (v) => <span className="num">{v}</span> },
    { title: 'CA / acheteuse', dataIndex: 'ca_par_acheteuse', key: 'cpa', align: 'right', render: (v) => <span className="num">{v == null ? '—' : money(v)}</span> },
    { title: 'Enregistrées', dataIndex: 'clientes', key: 'e', align: 'right', render: (v) => <span className="num">{v}</span> },
    { title: 'Silencieuses (période)', dataIndex: 'silencieuses', key: 'sil', align: 'right', render: (v, r) => (
      v > 0 && r.clientes > 0
        ? <b className="num pz-gisement">{v}</b>
        : <span className="num">{v}</span>
    ) },
  ];

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoePageHeader eyebrow="Rapports" title="Ventes manquées & zones" />
        <NdoeError title="Accès réservé" text="Ce rapport est réservé à l'administration." />
      </NdoePage>
    );
  }

  const guardePerso = period.period === 'custom' && !(period.start_date && period.end_date);

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Ventes manquées & zones"
        subtitle="Le chiffre d'affaires perdu faute de stock, et la carte de vos clientes par commune — où pousser, où activer."
      />

      <div className="pz-toolbar">
        <Segmented value={vue} onChange={setVue} options={[
          { value: 'pertes', label: 'Ventes manquées (ruptures)' },
          { value: 'zones', label: 'Zones (communes)' },
        ]} />
        <NdoePeriodPicker value={period} onChange={setPeriod} />
      </div>

      {error ? (
        <NdoeError title="Erreur" text={error} onRetry={guardePerso ? undefined : load} />
      ) : loading || !donnees ? (
        /* !donnees couvre le cas « Perso » sans dates après une bascule de
           volet : aucune requête ne part, on n'affiche JAMAIS les chiffres de
           l'autre volet (ni de faux zéros). */
        guardePerso && !loading
          ? <p className="pz-note">Choisissez les deux dates de la période « Perso » pour charger ce volet.</p>
          : <NdoeLoading rows={5} />
      ) : vue === 'pertes' ? (
        <>
          <NdoeStats>
            <NdoeStatCard label="CA manqué (estimation)" value={money(s.ca_manque_estime)} gold />
            <NdoeStatCard label="Paires manquées" value={s.paires_manquees ?? 0} />
            <NdoeStatCard label="Produits touchés" value={s.produits_touches ?? 0} />
            <NdoeStatCard label="CA réalisé (période)" value={s.ca_periode == null ? '—' : money(s.ca_periode)} />
          </NdoeStats>

          {(donnees?.pointures || []).length > 0 && (
            <p className="pz-hint">
              Pointures qui coûtent le plus :{' '}
              {(donnees.pointures || []).slice(0, 4).map((pt, i) => (
                <Tag key={i} color="volcano">P{pt.taille} · {pt.ventes_manquees} paire(s)</Tag>
              ))}
              <NdoeActionButton size="sm" variant="secondary" onClick={() => navigate('/rapports/pointures')}>Analyse pointures →</NdoeActionButton>
            </p>
          )}

          <div className="ndoe-card pz-block">
            <NdoeResponsiveTable
              data={donnees?.data || []}
              columns={colsPertes}
              rowKey="id_produit"
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
              empty={{ icon: '🎉', title: 'Aucune vente manquée détectée', text: 'Aucun produit qui vendait ne s’est retrouvé en rupture sur la période.' }}
            />
          </div>

          <p className="pz-note">
            <b>Estimation</b> : paires manquées = vitesse de vente d'avant-rupture × jours de rupture
            (plafonnée à 2× le volume déjà vendu, comptée seulement si les pointures sœurs vendaient encore) ;
            CA manqué = paires × prix moyen réellement pratiqué sur la période. Une boussole pour le réassort — pas une écriture comptable.
          </p>
        </>
      ) : (
        <>
          <NdoeStats>
            <NdoeStatCard label="Communes actives" value={s.communes_actives ?? 0} gold />
            <NdoeStatCard label="CA (période)" value={money(s.ca_total)} />
            <NdoeStatCard label="Top commune" value={s.top_commune || '—'} />
            <NdoeStatCard label="Meilleur panier" value={s.meilleur_panier ? `${s.meilleur_panier.commune} (${money(s.meilleur_panier.ca_par_acheteuse)})` : '—'} />
          </NdoeStats>

          <div className="ndoe-card pz-block">
            <NdoeResponsiveTable
              data={donnees?.zones || []}
              columns={colsZones}
              rowKey="commune"
              pagination={{ pageSize: 12 }}
              scroll={{ x: 'max-content' }}
              empty={{ icon: '🗺️', title: 'Aucune vente sur la période' }}
            />
          </div>

          <p className="pz-note">
            <b>Silencieuses</b> = clientes enregistrées dans la commune qui n'ont PAS acheté sur la période —
            le gisement à activer (voyez « Qui relancer » pour les contacter). Le CA / acheteuse dit où le panier est le plus fort.
          </p>
        </>
      )}
      {/* Le tiroir de la fiche paire, monte une fois pour la page. */}
      {ficheElement}
    </NdoePage>
  );
}
